// src/components/ui/Badge/Badge.tsx
import React, {useState} from 'react';
import {IonIcon} from '@ionic/react';

// ═══════════════════════════════════════════════════════════════════
// TYPES & INTERFACES
// ═══════════════════════════════════════════════════════════════════

/** Badge semantic variants aligned with brand guidelines */
export type BadgeVariant =
    | 'primary'      // Foundry Blue - Structure
    | 'secondary'    // Slate/Gray - Supporting info
    | 'cta'          // Forge Gold - Action
    | 'success'      // Timberline Green - Growth/Income
    | 'danger'       // Red - Expense/Error
    | 'warning'      // Gold variant for warnings
    | 'info'         // Blue variant for informational
    | 'neutral'      // Muted/Gray for default states
    | 'transfer'     // Purple for transfers
    | 'pending'      // Slate for pending states
    | 'business';    // Gold accent for business transactions

/** Badge visual styles */
export type BadgeFill =
    | 'solid'        // Filled background
    | 'soft'         // Lighter background with colored text
    | 'outline'      // Border only
    | 'ghost';       // Minimal, text only with subtle hover

/** Badge sizes */
export type BadgeSize =
    | 'xs'           // Extra small (for notification counts)
    | 'sm'           // Small (compact lists)
    | 'md'           // Medium (default)
    | 'lg';          // Large (emphasis)

/** Badge shape */
export type BadgeShape =
    | 'rounded'      // Pill shape (default)
    | 'square'       // Slightly rounded corners
    | 'dot';         // Circular dot indicator

export interface BadgeProps {
    /** Content to display */
    children?: React.ReactNode;
    /** Semantic variant */
    variant?: BadgeVariant;
    /** Visual fill style */
    fill?: BadgeFill;
    /** Size preset */
    size?: BadgeSize;
    /** Shape preset */
    shape?: BadgeShape;
    /** Optional icon (ionicons string) */
    icon?: string;
    /** Icon position */
    iconPosition?: 'start' | 'end';
    /** Custom background color (overrides variant) */
    customBg?: string;
    /** Custom text color (overrides variant) */
    customColor?: string;
    /** Show close/dismiss button */
    dismissible?: boolean;
    /** Callback when dismissed */
    onDismiss?: () => void;
    /** Click handler (makes badge interactive) */
    onClick?: () => void;
    /** Pulsing animation for attention */
    pulse?: boolean;
    /** Force hover state (for demos) */
    forceHover?: boolean;
    /** Additional className */
    className?: string;
    /** Inline styles */
    style?: React.CSSProperties;
    /** Aria label for accessibility */
    'aria-label'?: string;
}

// ═══════════════════════════════════════════════════════════════════
// VARIANT COLOR MAPPINGS
// ═══════════════════════════════════════════════════════════════════

interface ColorScheme {
    bg: string;
    bgSoft: string;
    color: string;
    colorSoft: string;
    border: string;
}

const variantColors: Record<BadgeVariant, ColorScheme> = {
    primary: {
        bg: 'var(--kff-foundry-blue)',
        bgSoft: 'rgba(27, 59, 90, 0.15)',
        color: '#ffffff',
        colorSoft: 'var(--kff-foundry-blue)',
        border: 'var(--kff-foundry-blue)',
    },
    secondary: {
        bg: 'var(--ion-color-step-200, #4a5568)',
        bgSoft: 'rgba(100, 116, 139, 0.15)',
        color: '#ffffff',
        colorSoft: 'var(--ion-color-medium, #64748b)',
        border: 'var(--ion-color-step-300, #64748b)',
    },
    cta: {
        bg: 'var(--kff-forge-gold)',
        bgSoft: 'rgba(203, 160, 82, 0.18)',
        color: '#0a1a2a',
        colorSoft: 'var(--kff-forge-gold)',
        border: 'var(--kff-forge-gold)',
    },
    success: {
        bg: 'var(--ff-income)',
        bgSoft: 'rgba(110, 231, 160, 0.18)',
        color: '#0a1a2a',
        colorSoft: 'var(--ff-income)',
        border: 'var(--ff-income)',
    },
    danger: {
        bg: 'var(--ff-expense)',
        bgSoft: 'rgba(252, 165, 165, 0.18)',
        color: '#0a1a2a',
        colorSoft: 'var(--ff-expense)',
        border: 'var(--ff-expense)',
    },
    warning: {
        bg: 'var(--kff-forge-gold)',
        bgSoft: 'rgba(203, 160, 82, 0.18)',
        color: '#0a1a2a',
        colorSoft: 'var(--kff-forge-gold)',
        border: 'var(--kff-forge-gold)',
    },
    info: {
        bg: 'var(--kff-foundry-blue)',
        bgSoft: 'rgba(27, 59, 90, 0.15)',
        color: '#ffffff',
        colorSoft: 'var(--kff-foundry-blue)',
        border: 'var(--kff-foundry-blue)',
    },
    neutral: {
        bg: 'var(--ion-color-step-300)',
        bgSoft: 'var(--ion-color-step-100)',
        color: 'var(--ion-text-color)',
        colorSoft: 'var(--ion-color-medium)',
        border: 'var(--ion-color-step-400)',
    },
    transfer: {
        bg: 'var(--ff-transfer)',
        bgSoft: 'rgba(167, 139, 250, 0.18)',
        color: '#ffffff',
        colorSoft: 'var(--ff-transfer)',
        border: 'var(--ff-transfer)',
    },
    pending: {
        bg: 'var(--ff-pending)',
        bgSoft: 'rgba(148, 163, 184, 0.18)',
        color: '#ffffff',
        colorSoft: 'var(--ff-pending)',
        border: 'var(--ff-pending)',
    },
    business: {
        bg: 'var(--ff-business)',
        bgSoft: 'rgba(203, 160, 82, 0.18)',
        color: '#0a1a2a',
        colorSoft: 'var(--ff-business)',
        border: 'var(--ff-business)',
    },
};

// ═══════════════════════════════════════════════════════════════════
// SIZE PRESETS
// ═══════════════════════════════════════════════════════════════════

interface SizePreset {
    fontSize: number;
    paddingY: number;
    paddingX: number;
    iconSize: number;
    gap: number;
    minHeight: number;
    dotSize: number;
}

const sizePresets: Record<BadgeSize, SizePreset> = {
    xs: {
        fontSize: 10,
        paddingY: 2,
        paddingX: 6,
        iconSize: 10,
        gap: 3,
        minHeight: 16,
        dotSize: 6,
    },
    sm: {
        fontSize: 11,
        paddingY: 3,
        paddingX: 8,
        iconSize: 12,
        gap: 4,
        minHeight: 20,
        dotSize: 8,
    },
    md: {
        fontSize: 12,
        paddingY: 5,
        paddingX: 12,
        iconSize: 14,
        gap: 5,
        minHeight: 26,
        dotSize: 10,
    },
    lg: {
        fontSize: 14,
        paddingY: 7,
        paddingX: 16,
        iconSize: 16,
        gap: 6,
        minHeight: 32,
        dotSize: 12,
    },
};

// ═══════════════════════════════════════════════════════════════════
// BADGE COMPONENT
// ═══════════════════════════════════════════════════════════════════

export const Badge: React.FC<BadgeProps> = ({
    children,
    variant = 'neutral',
    fill = 'solid',
    size = 'md',
    shape = 'rounded',
    icon,
    iconPosition = 'start',
    customBg,
    customColor,
    dismissible = false,
    onDismiss,
    onClick,
    pulse = false,
    forceHover = false,
    className,
    style,
    'aria-label': ariaLabel,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isDismissing, setIsDismissing] = useState(false);

    // Fallback to neutral if variant is undefined (defensive coding)
    const colors = variantColors[variant] || variantColors.neutral;
    const sizes = sizePresets[size] || sizePresets.md;
    const isInteractive = !!onClick || dismissible;
    const showHoverState = (isHovered || forceHover) && isInteractive;

    // Determine colors based on fill
    const getBackgroundColor = (): string => {
        if (customBg) return customBg;
        if (fill === 'solid') return colors.bg;
        if (fill === 'soft') return colors.bgSoft;
        if (fill === 'outline') return 'transparent';
        return 'transparent'; // ghost
    };

    const getTextColor = (): string => {
        if (customColor) return customColor;
        if (fill === 'solid') return colors.color;
        return colors.colorSoft;
    };

    const getBorderColor = (): string => {
        if (fill === 'outline') return colors.border;
        return 'transparent';
    };

    // Compute border radius
    const getBorderRadius = (): number => {
        if (shape === 'dot') return 999;
        if (shape === 'square') return 6;
        return 999; // pill for rounded
    };

    // Handle dismiss
    const handleDismiss = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsDismissing(true);
        setTimeout(() => {
            onDismiss?.();
        }, 150);
    };

    // Dot shape renders just a colored circle
    if (shape === 'dot') {
        return (
            <span
                className={className}
                role={ariaLabel ? 'status' : undefined}
                aria-label={ariaLabel}
                style={{
                    display: 'inline-block',
                    width: sizes.dotSize,
                    height: sizes.dotSize,
                    borderRadius: '50%',
                    background: customBg || colors.bg,
                    boxShadow: pulse ? `0 0 0 0 ${colors.bg}` : undefined,
                    animation: pulse ? 'badge-pulse 1.5s infinite' : undefined,
                    flexShrink: 0,
                    ...style,
                }}
            />
        );
    }

    const baseStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: sizes.gap,
        fontFamily: 'var(--kff-data-font)',
        fontSize: sizes.fontSize,
        fontWeight: 600,
        letterSpacing: '0.01em',
        lineHeight: 1,
        minHeight: sizes.minHeight,
        padding: `${sizes.paddingY}px ${sizes.paddingX}px`,
        borderRadius: getBorderRadius(),
        background: getBackgroundColor(),
        color: getTextColor(),
        border: `1.5px solid ${getBorderColor()}`,
        cursor: isInteractive ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: showHoverState ? 'scale(1.04)' : isDismissing ? 'scale(0.9)' : 'scale(1)',
        opacity: isDismissing ? 0 : 1,
        boxShadow: showHoverState && fill === 'solid'
            ? `0 4px 14px ${colors.bgSoft}`
            : undefined,
        ...style,
    };

    // Pulse keyframes injection (once)
    const pulseStyle = pulse ? (
        <style>{`
            @keyframes badge-pulse {
                0% { box-shadow: 0 0 0 0 ${colors.bgSoft}; }
                70% { box-shadow: 0 0 0 8px transparent; }
                100% { box-shadow: 0 0 0 0 transparent; }
            }
        `}</style>
    ) : null;

    const content = (
        <>
            {icon && iconPosition === 'start' && (
                <IonIcon icon={icon} style={{fontSize: sizes.iconSize, flexShrink: 0}} />
            )}
            {children}
            {icon && iconPosition === 'end' && (
                <IonIcon icon={icon} style={{fontSize: sizes.iconSize, flexShrink: 0}} />
            )}
            {dismissible && (
                <button
                    type="button"
                    onClick={handleDismiss}
                    aria-label="Dismiss"
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: sizes.iconSize + 2,
                        height: sizes.iconSize + 2,
                        padding: 0,
                        margin: 0,
                        marginLeft: 2,
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        color: 'inherit',
                        opacity: 0.7,
                        borderRadius: '50%',
                        transition: 'all 0.15s',
                    }}
                    onMouseEnter={e => {e.currentTarget.style.opacity = '1'; e.currentTarget.style.background = 'rgba(0,0,0,0.1)';}}
                    onMouseLeave={e => {e.currentTarget.style.opacity = '0.7'; e.currentTarget.style.background = 'transparent';}}
                >
                    <svg
                        width={sizes.iconSize - 2}
                        height={sizes.iconSize - 2}
                        viewBox="0 0 12 12"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                    >
                        <path d="M2 2L10 10M10 2L2 10" />
                    </svg>
                </button>
            )}
        </>
    );

    // Render as button if interactive
    if (onClick) {
        return (
            <>
                {pulseStyle}
                <button
                    type="button"
                    className={className}
                    onClick={onClick}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    aria-label={ariaLabel}
                    style={{...baseStyle, border: `1.5px solid ${getBorderColor()}`}}
                >
                    {content}
                </button>
            </>
        );
    }

    return (
        <>
            {pulseStyle}
            <span
                className={className}
                role={ariaLabel ? 'status' : undefined}
                aria-label={ariaLabel}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
                style={baseStyle}
            >
                {content}
            </span>
        </>
    );
};

Badge.displayName = 'Badge';

// ═══════════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPER: NOTIFICATION BADGE (dot with count)
// ═══════════════════════════════════════════════════════════════════

export interface NotificationBadgeProps {
    /** Notification count */
    count: number;
    /** Maximum count to display before showing "99+" */
    maxCount?: number;
    /** Size of the badge */
    size?: BadgeSize;
    /** Variant color */
    variant?: BadgeVariant;
    /** Show pulse animation */
    pulse?: boolean;
    /** Show dot only (no count) */
    dot?: boolean;
}

export const NotificationBadge: React.FC<NotificationBadgeProps> = ({
    count,
    maxCount = 99,
    size = 'xs',
    variant = 'danger',
    pulse = false,
    dot = false,
}) => {
    if (count <= 0) return null;

    if (dot) {
        return <Badge variant={variant} size={size} shape="dot" pulse={pulse} />;
    }

    const displayCount = count > maxCount ? `${maxCount}+` : String(count);

    return (
        <Badge variant={variant} size={size} fill="solid" pulse={pulse}>
            {displayCount}
        </Badge>
    );
};

NotificationBadge.displayName = 'NotificationBadge';

// ═══════════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPER: STATUS BADGE
// ═══════════════════════════════════════════════════════════════════

export type StatusType = 'success' | 'warning' | 'error' | 'info' | 'pending' | 'neutral';

export interface StatusBadgeProps {
    /** Status type */
    status: StatusType;
    /** Label text */
    children: React.ReactNode;
    /** Size */
    size?: BadgeSize;
    /** Show dot indicator before text */
    showDot?: boolean;
}

const statusToVariant: Record<StatusType, BadgeVariant> = {
    success: 'success',
    warning: 'warning',
    error: 'danger',
    info: 'info',
    pending: 'pending',
    neutral: 'neutral',
};

export const StatusBadge: React.FC<StatusBadgeProps> = ({
    status,
    children,
    size = 'sm',
    showDot = true,
}) => {
    const variant = statusToVariant[status];

    return (
        <Badge variant={variant} size={size} fill="soft">
            {showDot && (
                <span
                    style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: variantColors[variant].border,
                        flexShrink: 0,
                    }}
                />
            )}
            {children}
        </Badge>
    );
};

StatusBadge.displayName = 'StatusBadge';

// ═══════════════════════════════════════════════════════════════════
// CONVENIENCE WRAPPER: CATEGORY BADGE
// ═══════════════════════════════════════════════════════════════════

export interface CategoryBadgeProps {
    /** Category name */
    category: string;
    /** Optional custom color */
    color?: string;
    /** Size */
    size?: BadgeSize;
    /** Optional icon */
    icon?: string;
    /** Click handler */
    onClick?: () => void;
}

export const CategoryBadge: React.FC<CategoryBadgeProps> = ({
    category,
    color,
    size = 'sm',
    icon,
    onClick,
}) => {
    return (
        <Badge
            variant="neutral"
            fill="soft"
            size={size}
            icon={icon}
            customBg={color ? `${color}22` : undefined}
            customColor={color}
            onClick={onClick}
        >
            {category}
        </Badge>
    );
};

CategoryBadge.displayName = 'CategoryBadge';

export default Badge;
